import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Cookie, X } from "lucide-react";

export type CookieConsent = {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  ts: number;
};

const STORAGE_KEY = "mp-cookie-consent";
const OPEN_EVENT = "mp-open-cookie-settings";
const CHANGE_EVENT = "mp-cookie-consent-change";

/** Consent expiry: re-ask after 6 months (ΑΠΔΠΧ recommendation). */
const CONSENT_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 182; // ~6 months

export function getCookieConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    const ts = typeof parsed.ts === "number" ? parsed.ts : 0;
    // Treat expired consent as no choice so the banner re-appears.
    if (!ts || Date.now() - ts > CONSENT_MAX_AGE_MS) return null;
    return {
      necessary: true,
      functional: !!parsed.functional,
      analytics: !!parsed.analytics,
      ts,
    };
  } catch {
    return null;
  }
}

function saveConsent(consent: Omit<CookieConsent, "necessary" | "ts">) {
  if (typeof window === "undefined") return;
  const full: CookieConsent = { necessary: true, ...consent, ts: Date.now() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(full));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: full }));
}

/** Re-open the cookie preferences dialog (e.g. from the footer). */
export function openCookieSettings() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/** Read consent reactively (for gating non-essential scripts). */
export function useCookieConsent(): CookieConsent | null {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  useEffect(() => {
    setConsent(getCookieConsent());
    const onChange = () => setConsent(getCookieConsent());
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, []);
  return consent;
}

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // GDPR/ΑΠΔΠΧ: non-essential categories must default to OFF (no pre-ticked consent).
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    // Show banner only if no choice has been stored yet.
    if (!getCookieConsent()) setVisible(true);
    const openSettings = () => {
      const current = getCookieConsent();
      setFunctional(current ? current.functional : false);
      setAnalytics(current ? current.analytics : false);
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_EVENT, openSettings);
    return () => window.removeEventListener(OPEN_EVENT, openSettings);
  }, []);

  const acceptAll = () => {
    saveConsent({ functional: true, analytics: true });
    setVisible(false);
    setShowSettings(false);
  };
  const rejectAll = () => {
    saveConsent({ functional: false, analytics: false });
    setVisible(false);
    setShowSettings(false);
  };
  const savePrefs = () => {
    saveConsent({ functional, analytics });
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4">
      <div className="mx-auto max-w-3xl animate-pop-in overflow-hidden rounded-3xl border border-primary/30 bg-card shadow-2xl shadow-primary/10">
        <div className="flex items-start gap-3 p-5">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-primary to-amber-500 text-2xl shadow-md animate-bob">
            🍪
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h2 className="text-base font-bold">Λατρεύουμε τα cookies (και την ιδιωτικότητά σου)!</h2>
              {!showSettings && (
                <button
                  type="button"
                  onClick={rejectAll}
                  aria-label="Κλείσιμο και απόρριψη μη απαραίτητων"
                  className="-mr-1 -mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-muted"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Χρησιμοποιούμε cookies για να λειτουργεί σωστά το κατάστημα και — με τη συγκατάθεσή σου —
              για να βελτιώνουμε την εμπειρία σου. Μάθε περισσότερα στην{" "}
              <Link to="/cookies" className="font-medium text-primary-strong underline underline-offset-2">Πολιτική Cookies</Link>.
            </p>

            {showSettings && (
              <div className="mt-4 space-y-2">
                <PrefRow
                  emoji="🛡️"
                  title="Απαραίτητα"
                  desc="Πάντα ενεργά — χρειάζονται για τη λειτουργία του site."
                  checked
                  disabled
                />
                <PrefRow
                  emoji="⚙️"
                  title="Λειτουργικά"
                  desc="Θυμούνται προτιμήσεις & προϊόντα που είδες."
                  checked={functional}
                  onChange={setFunctional}
                />
                <PrefRow
                  emoji="📊"
                  title="Στατιστικά & Marketing"
                  desc="Ανώνυμα στατιστικά και σχετικές προτάσεις."
                  checked={analytics}
                  onChange={setAnalytics}
                />
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={acceptAll}
                className="mp-bouncy rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-md hover:opacity-90"
              >
                Αποδοχή όλων
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="mp-bouncy rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-md hover:opacity-90"
              >
                Απόρριψη όλων
              </button>
              {showSettings ? (
                <button
                  type="button"
                  onClick={savePrefs}
                  className="mp-bouncy rounded-full border border-primary/40 bg-primary/5 px-5 py-2.5 text-sm font-semibold text-primary-strong hover:bg-primary/10"
                >
                  Αποθήκευση επιλογών
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowSettings(true)}
                  className="rounded-full px-4 py-2.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
                >
                  Ρυθμίσεις
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrefRow({
  emoji,
  title,
  desc,
  checked,
  disabled,
  onChange,
}: {
  emoji: string;
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label
      className={`flex items-start gap-3 rounded-2xl border border-border p-3 ${disabled ? "opacity-80" : "cursor-pointer hover:border-primary/30"}`}
    >
      <span className="text-xl">{emoji}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">{title}</span>
        <span className="block text-xs text-muted-foreground">{desc}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-1 h-4 w-4 shrink-0 rounded border-border accent-primary"
      />
    </label>
  );
}
